// app/javascript/controllers/dashboard_controller.js
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register all Chart.js components and plugins
Chart.register(...registerables, zoomPlugin, ChartDataLabels);

document.addEventListener('turbo:load', () => {

  // --- Helper Functions ---
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // --- Main Sales Line Chart (Unchanged) ---
  const salesChartCtx = document.getElementById('salesChart');
  if (salesChartCtx) {
    // ... (This section is unchanged)
    const chartData = {
      day: { labels: JSON.parse(salesChartCtx.dataset.dayLabels), values: JSON.parse(salesChartCtx.dataset.dayValues) },
      week: { labels: JSON.parse(salesChartCtx.dataset.weekLabels), values: JSON.parse(salesChartCtx.dataset.weekValues) },
      month: { labels: JSON.parse(salesChartCtx.dataset.monthLabels), values: JSON.parse(salesChartCtx.dataset.monthValues) },
      year: { labels: JSON.parse(salesChartCtx.dataset.yearLabels), values: JSON.parse(salesChartCtx.dataset.yearValues) }
    };

    const salesChart = new Chart(salesChartCtx, {
      type: 'line',
      data: {
        labels: chartData.week.labels,
        datasets: [{
          label: 'Revenue',
          data: chartData.week.values,
          borderColor: 'rgba(78, 115, 223, 1)',
          backgroundColor: 'rgba(78, 115, 223, 0.05)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(78, 115, 223, 1)',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } },
          x: { grid: { display: false } }
        },
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (context) => `Revenue: ${formatCurrency(context.parsed.y)}` } },
          datalabels: { display: false },
          zoom: {
            pan: { enabled: true, mode: 'x' },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x',
              onZoomComplete: () => { document.getElementById('resetZoomBtn').style.display = 'inline-block'; }
            }
          }
        }
      }
    });

    const timeframeButtons = document.querySelectorAll('.btn-group[aria-label="Chart Timeframe"] button');
    timeframeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        timeframeButtons.forEach(btn => btn.classList.remove('active'));
        e.currentTarget.classList.add('active');
        const period = e.currentTarget.dataset.period;
        salesChart.data.labels = chartData[period].labels;
        salesChart.data.datasets[0].data = chartData[period].values;
        salesChart.update();
        salesChart.resetZoom();
        document.getElementById('resetZoomBtn').style.display = 'none';
      });
    });
    
    document.getElementById('resetZoomBtn')?.addEventListener('click', () => {
        salesChart.resetZoom();
        document.getElementById('resetZoomBtn').style.display = 'none';
    });
  }

  // --- Donut Chart: Sales by Country (Unchanged) ---
  const countryChartCtx = document.getElementById('countryChart');
  if (countryChartCtx) {
    // ... (This section is unchanged)
    new Chart(countryChartCtx, {
      type: 'doughnut',
      data: {
        labels: JSON.parse(countryChartCtx.dataset.labels),
        datasets: [{
          data: JSON.parse(countryChartCtx.dataset.values).map(Number),
          backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#5a5c69'],
          borderWidth: 2,
          hoverBorderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right', labels: { boxWidth: 12 } },
          tooltip: { callbacks: { label: (context) => `${context.label}: ${formatCurrency(context.parsed)}` } },
          datalabels: {
            color: '#fff',
            font: { weight: 'bold' },
            formatter: (value, ctx) => {
              const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
              if (sum === 0) return '';
              const percentage = (value * 100 / sum).toFixed(1) + "%";
              return percentage;
            }
          }
        },
        cutout: '70%'
      }
    });
  }

  // --- Bar Chart for Top Selling Products by Month ---
  const topProductsCtx = document.getElementById('topProductsChart');
  if (topProductsCtx) {
    const rawData = JSON.parse(topProductsCtx.dataset.chartData);
    const months = Object.keys(rawData);
    
    // --- FIX HERE: Expanded the color palette to prevent white/light bars ---
    const colorPalette = ['#4E73DF', '#1CC88A', '#36B9CC', '#F6C23E', '#E74A3B', '#845EC2', '#D65DB1', '#FF6F91', '#FF9671', '#FFC75F', '#008F7A', '#5A5C69'];
    
    let productColors = {};
    let colorIndex = 0;
    
    const allProducts = new Set();
    months.forEach(month => {
      rawData[month].forEach(product => allProducts.add(product.name));
    });

    allProducts.forEach(productName => {
      productColors[productName] = colorPalette[colorIndex % colorPalette.length];
      colorIndex++;
    });

    const datasets = Array.from(allProducts).map(productName => {
      return {
        label: productName,
        data: months.map(month => {
          const productData = rawData[month].find(p => p.name === productName);
          return productData ? productData.count : 0;
        }),
        backgroundColor: productColors[productName],
        borderColor: productColors[productName],
        borderWidth: 1
      };
    });

    new Chart(topProductsCtx, {
      type: 'bar',
      data: {
        labels: months,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            ticks: { precision: 0 }
          }
        },
        plugins: {
          legend: {
            position: 'top',
            labels: { boxWidth: 12, padding: 20 }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += `${context.parsed.y} sold`;
                }
                return label;
              }
            }
          },
          datalabels: {
            display: false
          }
        }
      }
    });
  }
});